import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Typography,
  Row,
  Col,
  Statistic,
  Spin,
  message
} from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  BookOutlined,
  TableOutlined
} from '@ant-design/icons';
import apiService from '@/services/apiService';
import TrialBalanceReport from './TrialBalanceReport';
import ProfitLossReport from './ProfitLossReport';
import BalanceSheetReport from './BalanceSheetReport';
import CashFlowReport from './CashFlowReport';
import GeneralLedgerReport from './GeneralLedgerReport';

const { Title, Text } = Typography;

interface FinancialSummary {
  ytd: {
    revenue: number;
    expenses: number;
    netIncome: number;
  };
  currentMonth: {
    revenue: number;
    expenses: number;
    netIncome: number;
  };
  balanceSheet: {
    totalAssets: number;
    totalLiabilities: number;
    totalEquity: number;
    balanced: boolean;
  };
}

const FinancialReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState('trial-balance');
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoadingSummary(true);
      const response = await apiService.get('/accounting/reports/summary');
      setSummary(response.data || response);
    } catch (error: any) {
      console.error('Failed to load summary:', error);
      // Don't show error message for summary - it's optional
    } finally {
      setLoadingSummary(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount));
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>Financial Reports</Title>
            <Text type="secondary">Comprehensive financial reporting and analysis</Text>
          </Col>
        </Row>
      </div>

      {/* Financial Summary Cards */}
      <Spin spinning={loadingSummary}>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="YTD Revenue"
                value={summary?.ytd?.revenue || 0}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: '#52c41a' }}
                prefix={<RiseOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="YTD Net Income"
                value={summary?.ytd?.netIncome || 0}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: (summary?.ytd?.netIncome || 0) >= 0 ? '#52c41a' : '#f5222d' }}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Assets"
                value={summary?.balanceSheet?.totalAssets || 0}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: '#1890ff' }}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Equity"
                value={summary?.balanceSheet?.totalEquity || 0}
                formatter={(value) => formatCurrency(Number(value))}
                valueStyle={{ color: '#722ed1' }}
                prefix={<PieChartOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* Report Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          items={[
            {
              key: 'trial-balance',
              label: (
                <span>
                  <TableOutlined />
                  Trial Balance
                </span>
              ),
              children: <TrialBalanceReport />
            },
            {
              key: 'profit-loss',
              label: (
                <span>
                  <BarChartOutlined />
                  Profit & Loss
                </span>
              ),
              children: <ProfitLossReport />
            },
            {
              key: 'balance-sheet',
              label: (
                <span>
                  <PieChartOutlined />
                  Balance Sheet
                </span>
              ),
              children: <BalanceSheetReport />
            },
            {
              key: 'cash-flow',
              label: (
                <span>
                  <LineChartOutlined />
                  Cash Flow
                </span>
              ),
              children: <CashFlowReport />
            },
            {
              key: 'general-ledger',
              label: (
                <span>
                  <BookOutlined />
                  General Ledger
                </span>
              ),
              children: <GeneralLedgerReport />
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default FinancialReports;

// Named exports for individual components
export { default as TrialBalanceReport } from './TrialBalanceReport';
export { default as ProfitLossReport } from './ProfitLossReport';
export { default as BalanceSheetReport } from './BalanceSheetReport';
export { default as CashFlowReport } from './CashFlowReport';
export { default as GeneralLedgerReport } from './GeneralLedgerReport';